import { useEffect, useRef, useState } from "react";
import { missingImage as missingResultsIcon } from "../../assets";
import { LoadingPlaceholder } from "../LoadingPlaceholder/LoadingPlaceholder";
import { SearchInput } from "../SearchInput/SearchInput";
import "./SearchCombobox.css";

export interface SearchComboboxProps<T> {
  /**
   * Initial value of the search input
   */
  initialValue: string;

  /**
   * Callback function when the input value changes
   */
  onChange: (value: string) => void;

  /**
   * Array of results to display in the dropdown
   */
  results?: T[];

  /**
   * Loading state indicator
   */
  isLoading?: boolean;

  /**
   * Whether the dropdown is open
   */
  isOpen?: boolean;

  /**
   * Size variant of the search input
   */
  size?: "md" | "lg";

  /**
   * Collapsible state of the search input
   */
  isCollapsible?: boolean;

  /**
   * Placeholder text for the search input
   */
  placeholder?: string;

  /**
   * Maximum height of the dropdown in pixels
   */
  maxDropdownHeight?: number;

  /**
   * Width of the dropdown in pixels. If not provided, matches input width
   */
  dropdownWidth?: number;

  /**
   * Render function for each result item
   */
  renderResult: (result: T) => React.ReactNode;

  /**
   * Callback when a result is selected
   */
  onResultSelect?: (result: T) => void;

  /**
   * Callback when clicking outside the combobox
   */
  onOutsideClick?: () => void;

  /**
   * Additional CSS class for the container
   */
  className?: string;

  /**
   * Reference to container element for positioning
   */
  containerRef?: React.RefObject<HTMLDivElement>;

  /**
   * Test id for the component
   */
  "data-testid"?: string;
}

/**
 * SearchCombobox component
 *
 * A composite component that combines a search input with a dropdown list of results.
 * Handles common search UI patterns like loading states, empty states, and keyboard navigation.
 *
 * @example
 * ```tsx
 * <SearchCombobox
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   results={searchResults}
 *   isLoading={isSearching}
 *   renderResult={(result) => (
 *     <SearchResult
 *       title={result.title}
 *       subtitle={result.subtitle}
 *       tags={result.tags}
 *     />
 *   )}
 * />
 * ```
 */
export const SearchCombobox = <T,>({
  initialValue,
  onChange,
  results = [],
  isLoading = false,
  isOpen = false,
  size = "lg",
  isCollapsible = false,
  placeholder = "Search",
  maxDropdownHeight = 400,
  dropdownWidth,
  renderResult,
  onResultSelect,
  onOutsideClick,
  className = "",
  containerRef,
  "data-testid": dataTestId,
}: SearchComboboxProps<T>) => {
  const localRef = useRef<HTMLDivElement>(null);
  const ref = containerRef || localRef;
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Track the currently highlighted result index
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  // Reset highlighted index when results change or dropdown closes
  useEffect(() => {
    if (!isOpen || results.length === 0) {
      setHighlightedIndex(-1);
    }
  }, [isOpen, results]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle keyboard navigation when dropdown is open and has results
    if (!isOpen || results.length === 0 || isLoading) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1,
        );
        break;

      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          onResultSelect?.(results[highlightedIndex]);
        }
        break;

      case "Escape":
        e.preventDefault();
        // Optionally close the dropdown - would need onClose callback
        setHighlightedIndex(-1);
        onOutsideClick?.();
        break;
    }
  };

  // Auto-scroll to keep highlighted item visible
  useEffect(() => {
    if (highlightedIndex >= 0 && resultsContainerRef.current) {
      const highlightedElement = resultsContainerRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        // Close dropdown when clicking outside
        // You might want to add onClose callback if needed
        onOutsideClick?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, onOutsideClick]);

  return (
    <div
      className={`indigo-search-combobox ${className}`}
      ref={ref}
      data-testid={dataTestId}
    >
      <SearchInput
        initialValue={initialValue}
        onChange={(newValue) => onChange(newValue)}
        placeholder={placeholder}
        size={size}
        collapsible={isCollapsible}
        isLoading={isLoading}
        onKeyDown={handleKeyDown}
        renderLoadingIndicator={() => (
          <LoadingPlaceholder width={20} height={20} />
        )}
      />

      {(isOpen || isLoading) && (
        <div
          className="indigo-search-combobox__dropdown"
          style={{
            maxHeight: maxDropdownHeight,
            width: dropdownWidth,
          }}
        >
          <div className="indigo-search-combobox__results-container">
            <div
              className="indigo-search-combobox__results"
              ref={resultsContainerRef}
            >
              {isLoading ? (
                <div className="indigo-search-combobox__loading">
                  <LoadingPlaceholder />
                </div>
              ) : results.length > 0 ? (
                results.map((result, index) => (
                  <div
                    key={index}
                    className={`indigo-search-combobox__result ${
                      highlightedIndex === index ? "is-highlighted" : ""
                    }`}
                    aria-selected={highlightedIndex === index}
                  >
                    {renderResult(result)}
                  </div>
                ))
              ) : (
                <div className="indigo-search-combobox__empty">
                  <img
                    src={missingResultsIcon}
                    alt="No results"
                    width="64"
                    height="64"
                    className="indigo-search-combobox__empty-image"
                  />
                  <div className="indigo-search-combobox__empty-title">
                    No results found
                  </div>
                  <div className="indigo-search-combobox__empty-subtitle">
                    Please try again
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchCombobox;
